/**
 * scarlett-freight.js — PDP bulky-freight CALCULATOR preview.
 *
 * DRAFT-THEME PREVIEW ARTIFACT ONLY (build/theme/INSTALL.md). Paired with
 * snippets/scarlett-freight-notice.liquid. The charges this widget shows are
 * PROPOSED and are NOT active at checkout, so a persistent banner says exactly
 * that above the input and is visible the whole time the block is shown.
 *
 * ONE CLOSING SENTENCE (2026-09-09). Every tier that names a figure --
 * priced, exact quote, similar-items, rough/typical and kit -- now ends with
 * the SAME sentence, and there is exactly one switch that chooses which form:
 * `freight_live`, assigned in the snippet and read here off the container's
 * `data-freight-live`. False (the draft default) closes "Preview only; this
 * proposed charge is not active at checkout."; true closes "The delivery
 * charge is shown at checkout before you pay." Before this pass four tiers
 * carried four different closings, one of which (the kit tier's) already
 * promised a live checkout charge. See the CLOSING block and setFreightLive().
 *
 * `unmeasured` is the ONE exception, and it is one on purpose: that state has
 * no figure to disclaim, so it closes with the checkout sentence in BOTH
 * modes -- true today (checkout already shows the standard rate) and true
 * after go-live. See unmeasuredText().
 *
 * No cookies, no localStorage/sessionStorage, no metafield/app/file reference.
 * The only two network destinations are (a) this theme's own same-origin
 * static classes JSON and (b) the one freight quote endpoint whose base URL is
 * handed to this script on the container's data-quote-endpoint attribute.
 *
 * ---------------------------------------------------------------------
 * Data consumed
 *
 * assets/scarlett-freight-classes.json  (built by build/theme/build_assets.py)
 *   Flat object: { "<SKU>": "C" | "D" | "E", ... }. A SKU ABSENT from this
 *   file has no bulky-freight class at all -> the whole block stays hidden for
 *   it, exactly as in the previous coverage-only version of this script.
 *
 * The quote endpoint (base URL from data-quote-endpoint; a colleague's
 * Cloudflare Worker at https://freight.scarlettmusic.com.au/quote):
 *
 *   GET <base>?sku=<sku>&postcode=<4 digits>&quantity=<1..9>
 *   200 -> { "status": "...", "charge_cents": 1234, "currency": "AUD",
 *            "quantity": 2, "release_version": "...", "preview": true,
 *            "allowance_cents": 10000, "evidence": "estimate",
 *            "carrier_count": 1, "carton": "measured" }
 *
 *   `carton` (added 2026-09-09) is what is known about THIS product's own
 *   packed box, which is a different question from `evidence` (how strong
 *   the FIGURE is) and from `status` (what this postcode resolved to):
 *   "measured" (a real carton -- the SKU has a parcels record or an exact
 *   real-carton quote), "estimated" (no carton of its own; the answer stood
 *   in another product's box), "none" (nothing about the packaging bore on
 *   the answer). It drives ONE thing: which noun the `unmeasured` sentence
 *   says it cannot price. See unmeasuredText() -- 76MX412AEF prices at 6718
 *   and used to claim "we can't estimate THIS ITEM" at 4870.
 *
 *   status is one of: protected | priced |
 *                     priced_single_carrier (aka priced_upper_bound) |
 *                     within_allowance | unmeasured | carrier_refused |
 *                     unavailable | invalid_postcode | invalid_quantity
 *
 *   2026-09-09 NINETEENTH PASS — ALLIED-ONLY BASIS. Kyal: "I wouldn't worry
 *   much about TGE - just base it off allied". The engine now prices from a
 *   SINGLE enabled carrier, so a priced line comes back as `priced` with
 *   `carrier_count` 1 and `priced_single_carrier` should no longer occur at
 *   all. It is still ACCEPTED (as is the older `priced_upper_bound`
 *   spelling), defensively, in case an older Worker build or a future
 *   re-tender brings it back — and it now renders EXACTLY the same copy as
 *   `priced`, because the copy no longer draws a distinction between "one
 *   carrier" and "the cheapest of several".
 *
 *   Every sentence implying an ongoing comparison is GONE — the old "cheapest
 *   of the ones we checked" framing and the promise that further quotes might
 *   yet reduce or remove the surcharge. Both described work that no longer
 *   happens, and dangling a reduction the shopper will never receive is worse
 *   than saying nothing. The copy still NEVER NAMES THE CARRIER — the
 *   endpoint sends no carrier name, so the widget has nothing to name it
 *   from; it says "our carrier's", which stays true through a re-tender.
 *
 *   `evidence` (added 2026-09-08 twelfth pass; extended 2026-09-09) is
 *   "exact_quote" (a real carrier quote for this exact product at this
 *   exact postcode), "estimate" (a figure interpolated from that carrier's
 *   fitted line for this item's carton family), or "estimate_similar_items"
 *   (this SKU has no measured carton of its own, so the figure is the
 *   median of comparable measured sibling products' actual freight costs at
 *   this postcode), or "estimate_class_typical" (added 2026-09-09
 *   SEVENTEENTH PASS — the ROUGH tier: nothing about this item's own packed
 *   size is known and even the sibling median had no validated evidence, so
 *   the figure comes from the TYPICAL MEASURED CARTON of its (freight class,
 *   product type) group — a real packed box belonging to a DIFFERENT product
 *   of the same kind. Kyal: "for the items that have the postage as 'not
 *   available yet' we're going to take a 'close enough' approach"). It — not
 *   the status, and never an assumption about
 *   WHICH carrier — decides whether a figure is worded as a quote or as
 *   an estimate. The copy table:
 *
 *     `priced` and `priced_single_carrier` share ONE copy table — they go
 *     through the same builder (pricedTextFor) and can never drift apart.
 *     <CLOSING> below is the ONE shared closing sentence every tier ends
 *     with — "Preview only; this proposed charge is not active at checkout."
 *     while `freight_live` is false, "The delivery charge is shown at
 *     checkout before you pay." once it is true:
 *
 *     ...        + exact_quote           -> "Freight preview: $X surcharge
 *                                           for this item, based on our
 *                                           carrier's quote. <CLOSING>"
 *     ...        + estimate              -> "Freight preview: estimated $X
 *                                           surcharge for this item, based
 *                                           on our carrier's pricing for
 *                                           this item's carton. <CLOSING>"
 *     ...        + estimate_similar_items -> PREFIXED "Estimated from
 *                                           similar items: ", then the same
 *                                           estimate sentence WITHOUT the
 *                                           carton clause: "...based on our
 *                                           carrier's pricing." A
 *                                           sibling-median figure exists
 *                                           precisely because this item has
 *                                           no measured carton, so naming
 *                                           one would be false.
 *     within_allowance     + estimate_similar_items -> the within_allowance
 *                                                    copy, PREFIXED
 *                                                    "Estimated from similar
 *                                                    items: "
 *     priced / priced_single_carrier + estimate_class_typical -> ITS OWN
 *                                                    sentence, not a prefix
 *                                                    on the estimate copy:
 *                                                    "Rough freight preview:
 *                                                    approximately $X
 *                                                    surcharge for this item
 *                                                    (or "for N items" above
 *                                                    quantity 1), based on
 *                                                    typical <label>
 *                                                    packaging. Your item's
 *                                                    packed size has not been
 *                                                    confirmed, so the amount
 *                                                    may change. <CLOSING>"
 *                                                    <label> is the
 *                                                    endpoint's
 *                                                    `typical_label`
 *                                                    ("digital piano"), or
 *                                                    "similar-item" when it
 *                                                    is absent.
 *     within_allowance     + estimate_class_typical -> "This typical-item
 *                                                    calculation gives no
 *                                                    bulky surcharge. Your
 *                                                    item's packed size has
 *                                                    not been confirmed, so a
 *                                                    surcharge may still
 *                                                    apply. <CLOSING>"
 *                                                    — deliberately NOT the
 *                                                    ordinary
 *                                                    within_allowance copy,
 *                                                    which states a settled
 *                                                    fact this tier cannot.
 *     estimate_uncertain (its own status, any evidence) -> "Freight to this
 *                                                    postcode may attract a
 *                                                    bulky-item surcharge
 *                                                    for this item: the
 *                                                    surcharge could be $0
 *                                                    or approximately
 *                                                    $LOW–$HIGH. Preview
 *                                                    only; these proposed
 *                                                    charges are not active
 *                                                    at checkout." — never
 *                                                    prefixed, never
 *                                                    appends the checkout
 *                                                    line
 *
 *   An absent/unrecognised `evidence` reads as "estimate" — the wording that
 *   promises least. `exact_quote` always wins over any other evidence value
 *   a malformed body might also carry: an estimate from sibling items can
 *   never BE an exact quote, so the "Estimated from similar items: " prefix
 *   is only ever added when `evidence` resolves to exactly
 *   `"estimate_similar_items"`, never alongside `exact_quote` wording. The
 *   same holds for `"estimate_class_typical"`, which additionally publishes
 *   NO range: the engine fits no interval for that tier (there is no hold-out
 *   to fit one on), and an invented band would read to a shopper exactly like
 *   a measured one.
 *   THE COPY NEVER NAMES A CARRIER: the endpoint does not send one, and the
 *   previous version's "using the Allied quote for this item" was an
 *   attribution error the moment a figure could come from TGE or from a
 *   fitted line. `carrier_count` (1 or 2) is accepted and currently unused
 *   for copy — the status already carries that distinction.
 *
 *   `estimate_uncertain` (added 2026-09-09) appears when the engine's
 *   CALIBRATED prediction-error interval around an `estimate_similar_items`
 *   figure STRADDLES the allowance — we genuinely cannot say which
 *   side of the threshold this item lands on. (Before the sixteenth pass the
 *   test was the donor group's own p25..p75; that band described how alike
 *   those siblings were, not how wrong this estimate might be.) For this
 *   status ONLY, the body also carries `range_low_cents` and
 *   `range_high_cents` (integers, cents, already rounded, already multiplied
 *   by quantity) — the CHARGES at the two interval bounds, so the low one is
 *   0 whenever the low bound sits inside the allowance. `charge_cents` is
 *   still present (the median) but this status's copy never uses it.
 *
 *   The copy states the two possible outcomes and never a single "up to"
 *   figure: "up to $X" reads as a ceiling the shopper will be held to, and
 *   the honest shape of this answer is that the surcharge could equally be
 *   nothing. When the low bound charges $0 (the common case) the copy says
 *   "could be $0 or approximately $HIGH"; when both bounds charge, it says
 *   "approximately $LOW–$HIGH".
 *
 *   It must never promise anything about checkout, so unlike the other
 *   `priced*` states it does NOT append `priced_line2` — it carries its own
 *   preview disclaimer instead. If BOTH range figures are missing or
 *   malformed (an older engine that has the status but no band), the widget
 *   falls back to the INTERIM wording, which states the uncertainty without
 *   inventing a range; it does not show the generic error, because the
 *   server told us precisely what kind of gap this is.
 *
 *   `unavailable` is NOT a delivery restriction — the endpoint also returns it
 *   for an unknown SKU and for a calculation error, so its copy says only that
 *   we cannot estimate yet. `carrier_refused` is the status that reflects a
 *   real refusal to deliver.
 *   HTTP 429 -> rate limited (its own message)
 *   HTTP 400 -> bad input; if the body still carries a known status that
 *               status's message is shown, otherwise the generic error
 *   anything else non-2xx, a network failure, or the 6s timeout -> generic error
 *
 * NOTE ON MONEY: no dollar figure is ever COMPUTED here. The only arithmetic
 * this file performs on money is dividing a server-supplied cents integer by
 * 100 to render it (charge_cents for `priced` / `priced_single_carrier`; the
 * two interval bounds for `estimate_uncertain`; and, since the 2026-09-09
 * TWENTIETH PASS, `allowance_cents` in the `within_allowance` sentence). The
 * allowance is NO LONGER hard-coded in the copy: Kyal moved it from $150 to
 * $100 on 9 Sep, and a figure baked into a theme asset is a second source of
 * truth that silently goes stale the next time the engine moves it. The
 * server sends the number it actually applied and the sentence renders THAT;
 * ALLOWANCE_FALLBACK_LABEL is used only when the body carries no usable
 * allowance_cents at all (an older Worker build).
 * Every money value is gated by isValidCents first: a charge that is negative,
 * fractional, non-finite, absent or not a number renders the generic error
 * state, never a dollar figure. The single-carrier figure is rendered verbatim
 * — this script never widens, rounds or hedges it. No margin, no freight maths,
 * no allowance comparison — the server owns every one of those decisions and
 * this script only renders the status it is handed.
 * ---------------------------------------------------------------------
 *
 * Flow: find the CURRENT variant's SKU (initial render, or after a variant
 * change with no page reload via the standard Dawn/Horizon hidden
 * `input[name="id"]` inside the product form, which fires a bubbling native
 * "change" event on every variant swap), look up its freight class, and only
 * then reveal the block (a SKU with no C/D/E class — the majority of the
 * catalogue — never shows the block at all; standard delivery terms apply to
 * it same as any other product, with no widget needed to say so).
 *
 * A quote is fetched on postcode submit, and thereafter again on a variant
 * change or a quantity change — but ONLY once the shopper has submitted a
 * valid postcode at least once, so nothing fires before there is an address to
 * price against. Quantity changes are debounced 400ms so a held-down stepper
 * does not spam the Worker; variant changes and explicit submits are not.
 *
 * The quantity sent is the one the shopper actually selected in the theme's
 * own quantity input, never a substitute. The endpoint prices 1..9 (see
 * MAX_QUANTITY, which MUST equal the Worker's QUOTE_MAX_QUANTITY); above
 * that the widget makes NO request and says so, because the alternative --
 * quoting a smaller quantity than the shopper chose and printing the figure
 * as if it were theirs -- understates the charge checkout will apply.
 * At most one request is ever in flight: a newer trigger ABORTS the older
 * request (AbortController) and a request-token check means a late response can
 * never overwrite a fresher rendered state.
 *
 * Never throws on a missing SKU, an in-flight variant change, a malformed
 * postcode, or a malformed response body — worst case the block stays hidden
 * or the generic error message is shown.
 *
 * Testability: the pure functions below are exported both as
 * `window.ScarlettFreight` (for build/theme/test_calculator.html, a plain
 * <script> page with a stubbed fetch) and via module.exports (for
 * build/theme/test_calculator.js under Node). The DOM wiring stays inside the
 * IIFE and runs only when a document is present.
 */
(function (root) {
  "use strict";

  /* ------------------------------------------------------------------ *
   * The allowance, 2026-09-09 TWENTIETH PASS
   *
   * Kyal, 9 Sep: "make the allowance for rural suburbs $100" (it was $150),
   * "and run on the unmeasured cartons too". The number therefore MOVES, and
   * a theme asset that hard-codes it becomes a second source of truth that
   * goes stale the moment the engine is retuned again -- while still reading,
   * to a shopper, exactly like a promise.
   *
   * So the widget states the allowance THE SERVER SAYS IT APPLIED. Every
   * /quote body already carries `allowance_cents`; the within-allowance
   * sentence renders that, and falls back to the label below ONLY when the
   * body carries no usable value at all (an older Worker build).
   *
   * NOTE: this is the BULKY-FREIGHT allowance per item. It is a different
   * number from the site-wide FREE SHIPPING THRESHOLD on products, which is
   * still $150 and lives in the theme's own settings -- nothing in this file
   * touches or restates that.
   * ------------------------------------------------------------------ */
  var ALLOWANCE_FALLBACK_LABEL = "$100";

  /* ------------------------------------------------------------------ *
   * Express (12 Oct 2026, Kyal-approved)
   *
   * At checkout, a cart of ONLY bulky items is offered Express = the
   * Standard freight + a flat uplift. This widget shows that Express price
   * beside its Standard figure. The uplift is ONE constant and MUST equal the
   * Worker's BULKY_EXPRESS_UPLIFT_CENTS (default 1500). Express is derived
   * from the SAME server figure the Standard line states (charge_cents), with
   * the SAME hedging word, and is omitted wherever there is no figure.
   * No delivery timeframe is stated for bulky Express (Kyal's choice).
   *
   * Where the Standard line is the ordinary flat rate (protected /
   * within_allowance) the Standard is $7, or free at the free-shipping
   * threshold, so those two numbers are the only other facts used. The
   * threshold is the site setting rendered by the snippet
   * (data-free-threshold-cents), with a fallback.
   * ------------------------------------------------------------------ */
  var EXPRESS_UPLIFT_CENTS = 1500;
  var STANDARD_FLAT_CENTS = 700;
  var FREE_THRESHOLD_FALLBACK_CENTS = 15000;
  var EXPRESS_NOTE = "Express applies to orders of bulky items only.";

  /**
   * Render a server-supplied allowance as the SHORT dollar label the sentence
   * wants: whole dollars with no trailing ".00" (10000 -> "$100"), but exact
   * cents whenever they are non-zero (10050 -> "$100.50"), because dropping
   * real cents would misstate the figure.
   *
   * Returns null for anything isValidCents rejects and for 0 -- a zero
   * allowance cannot co-exist with a `within_allowance` verdict, so a body
   * claiming both is malformed, and "within our $0 allowance" is a sentence
   * that must never reach a shopper. Callers treat null as "fall back".
   * @param {*} cents
   * @returns {string|null}
   */
  function formatAllowance(cents) {
    if (!isValidCents(cents)) return null;
    if (cents === 0) return null;
    var dollars = Math.floor(cents / 100);
    var remainder = cents % 100;
    if (remainder === 0) return "$" + dollars;
    return "$" + dollars + "." + (remainder < 10 ? "0" + remainder : String(remainder));
  }

  /**
   * The within-allowance sentence for a given allowance label.
   * @param {string} label e.g. "$100"
   * @returns {string}
   */
  function withinAllowanceText(label) {
    return (
      "No bulky freight surcharge: estimated freight is within our " +
      label +
      " allowance per item."
    );
  }

  /**
   * The within-allowance sentence for a whole response body: the server's own
   * allowance when it sent a usable one, the fallback label otherwise.
   * @param {Object} json
   * @returns {string}
   */
  function withinAllowanceTextFor(json) {
    var label = json ? formatAllowance(json.allowance_cents) : null;
    return withinAllowanceText(label || ALLOWANCE_FALLBACK_LABEL);
  }

  /* ------------------------------------------------------------------ *
   * THE ONE CLOSING SENTENCE, 2026-09-09
   *
   * Every tier used to end its own way -- "Preview only; this proposed charge
   * is not active at checkout." on the priced tiers, "This proposed charge is
   * not active at checkout." (no "Preview only;") on the rough tier, a bare
   * " Preview only." on its $0, and "Preview only -- the amount payable is
   * shown at checkout." on the kit tier, which had already been written for
   * a LIVE mechanism. A shopper comparing two products was reading two
   * different promises about the same checkout.
   *
   * There is now exactly ONE closing sentence, and exactly one switch that
   * chooses which of its two forms every tier ends with:
   *
   *   FREIGHT_LIVE false (default) -- the charges are proposals shown on a
   *     draft theme and nothing is charged at checkout. Say so.
   *   FREIGHT_LIVE true            -- the CarrierService is registered and
   *     the Bulky profile is live, so checkout DOES quote freight. The
   *     preview sentence would then be a lie, and this one is the truth.
   *
   * The switch is NOT decided here: `snippets/scarlett-freight-notice.liquid`
   * assigns `freight_live` (false on the draft) and passes it on the
   * container as `data-freight-live`; initNotice() reads it and calls
   * setFreightLive() before anything renders. That one Liquid assign is the
   * whole go-live copy swap -- see GO-LIVE.md Section 6.
   * ------------------------------------------------------------------ */
  var CLOSING_PREVIEW = "Preview only; this proposed charge is not active at checkout.";
  var CLOSING_LIVE = "The delivery charge is shown at checkout before you pay.";

  /** `estimate_uncertain`'s own preview form. It is the ONE tier that names
   * two possible figures rather than one, so its preview sentence stays
   * PLURAL ("these proposed charges") -- the singular would misdescribe what
   * the sentence above it just said. Once freight is live it takes the same
   * shared sentence as everything else, which is number-agnostic. */
  var CLOSING_PREVIEW_PLURAL = "Preview only; these proposed charges are not active at checkout.";

  var FREIGHT_LIVE = false;

  /** The shared closing sentence for the current mode. */
  function closingSentence() {
    return FREIGHT_LIVE ? CLOSING_LIVE : CLOSING_PREVIEW;
  }

  /** The `estimate_uncertain` closing sentence for the current mode. */
  function closingSentencePlural() {
    return FREIGHT_LIVE ? CLOSING_LIVE : CLOSING_PREVIEW_PLURAL;
  }

  /**
   * Flip the widget between preview and live wording. Called once per notice
   * from initNotice() (from `data-freight-live`) and directly by the tests.
   * Anything that is not exactly `true` means preview -- a missing, mistyped
   * or malformed attribute must never publish a checkout promise.
   * @param {*} next
   */
  function setFreightLive(next) {
    FREIGHT_LIVE = next === true;
    refreshMessages();
  }

  /** Re-derive every fixed message that ends in the shared closing sentence.
   * The dynamic sentences (priced/rough/kit) call closingSentence() when they
   * are built, so they need nothing here. */
  function refreshMessages() {
    MESSAGES.priced_line2 = closingSentence();
    MESSAGES.estimate_uncertain_preview_note = closingSentencePlural();
    MESSAGES.estimate_uncertain_no_range =
      "Similar-item estimates vary enough that we can't yet tell whether a" +
      " bulky surcharge applies. " +
      closingSentencePlural();
    // `unmeasured` is deliberately NOT rebuilt here -- its closing sentence
    // is the same in both modes. See unmeasuredText().
  }

  /**
   * The `unmeasured` sentence, 2026-09-09.
   *
   * The old copy said "for this ITEM" unconditionally, which was wrong
   * whenever the item HAS a measured carton and only this destination has no
   * usable line for it. 76MX412AEF is the case that exposed it: it prices at
   * $480 to 6718, and at 4870 the same product read "we can't show a reliable
   * freight estimate for this item yet" -- a claim about the product, made by
   * a widget that had just priced the product.
   *
   * The endpoint's `carton` field settles which of the two is true, so the
   * sentence now names the thing that is actually unknown. Only an explicit
   * "measured" earns the narrower wording; an older Worker build that sends
   * no `carton` at all falls back to "this item", which is what shipped
   * before and claims least.
   *
   * THE ONE TIER THAT DOES NOT FOLLOW `freight_live`. Every other sentence in
   * this widget ends with closingSentence(), which says "Preview only; this
   * proposed charge is not active at checkout." while freight is not live.
   * `unmeasured` cannot say that: there IS no proposed charge in this state
   * -- the widget has just told the shopper it has no figure -- so the
   * preview disclaimer would be disclaiming something that does not exist.
   *
   * It closes with CLOSING_LIVE in BOTH modes instead, and that sentence is
   * true TODAY as well as after go-live: checkout already shows the shopper
   * the standard delivery rate before they pay. This is the honest answer in
   * the one state where the shared switch has nothing to switch.
   *
   * @param {string} carton "measured" | "estimated" | "none"
   * @returns {string}
   */
  function unmeasuredText(carton) {
    return (
      "We can't show a reliable freight estimate for " +
      (carton === "measured" ? "this postcode" : "this item") +
      " yet. " +
      CLOSING_LIVE
    );
  }

  /**
   * What the endpoint says is known about THIS product's own packed box:
   * "measured" (a real carton -- a `sku_parcels.json` entry or an exact
   * real-carton quote), "estimated" (no carton of its own; the answer stood
   * in another product's box), or "none" (nothing about the item's packaging
   * bore on the answer).
   *
   * **Anything absent or unrecognised reads as "none"** -- same doctrine as
   * resolveEvidence(): only an explicit value earns different copy, so an
   * older Worker build, a cached response or a malformed body can never make
   * this widget narrow a claim it has not been told it may narrow.
   * @param {Object} json
   * @returns {string} "measured" | "estimated" | "none"
   */
  function resolveCarton(json) {
    if (json && json.carton === "measured") return "measured";
    if (json && json.carton === "estimated") return "estimated";
    return "none";
  }

  /** Exact customer-facing copy. Do not reword without sign-off. */
  var MESSAGES = {
    // 2026-09-09 twenty-first pass. Kyal: "yep I think we add outer
    // regional", so the surchargeable set is now the ABS Outer Regional,
    // Remote and Very Remote classes; capital cities, major regional cities
    // and inner regional areas stay free. The sentence states the checked
    // postcode's own answer and then names the rule that produced it, so it
    // over-claims in neither direction. It is about the CURRENT bulky-item
    // policy, not an open-ended promise about this postcode forever.
    protected:
      "No bulky surcharge applies to this postcode. Only outer regional," +
      " remote and very remote areas are surcharged.",
    // THE shared closing sentence. Written in exactly one place
    // (closingSentence()) and re-derived by refreshMessages() whenever
    // setFreightLive() flips the mode -- see the CLOSING block above.
    priced_line2: closingSentence(),
    // `estimate_uncertain`'s own closing. PLURAL in preview mode ("these
    // proposed charges") because this status names two possible figures, not
    // one; identical to every other tier once freight is live.
    estimate_uncertain_preview_note: closingSentencePlural(),
    // The interim wording, used when the status arrives without a usable
    // band (an engine older than the calibrated interval). It says the one
    // true thing available -- that comparable items straddle the threshold
    // -- and names no amount at all.
    estimate_uncertain_no_range:
      "Similar-item estimates vary enough that we can't yet tell whether a" +
      " bulky surcharge applies. " +
      closingSentencePlural(),
    // The allowance figure is NOT part of this string -- see
    // withinAllowanceText() below. This entry is the fallback sentence, built
    // from ALLOWANCE_FALLBACK_LABEL, and is what a body with no usable
    // allowance_cents renders.
    within_allowance: withinAllowanceText(ALLOWANCE_FALLBACK_LABEL),
    // The two `unmeasured` forms -- see unmeasuredText(). `unmeasured` is the
    // default ("for this item"); `unmeasured_postcode` is the narrower one a
    // response with `carton: "measured"` earns. Both are FIXED in either
    // mode (this is the one tier `freight_live` does not switch), and
    // messageFor() picks between them per response rather than reaching them
    // through the generic build() fallthrough.
    unmeasured: unmeasuredText("none"),
    unmeasured_postcode: unmeasuredText("measured"),
    carrier_refused:
      "This carrier cannot deliver this item here. Other delivery options are not yet confirmed.",
    // NOT a delivery restriction: the endpoint also returns `unavailable` for
    // an unknown SKU and for a calculation error, so this must not tell the
    // shopper their address is the problem. `carrier_refused` is the status
    // that DOES reflect a real refusal -- its copy is deliberately different.
    unavailable:
      "We can't estimate this item yet; delivery availability is not confirmed.",
    invalid_postcode: "Enter a 4-digit Australian postcode.",
    invalid_quantity: "Enter a valid quantity (1-9).",
    rate_limited: "Too many checks. Please wait a moment and try again.",
    // No checkout promise here: this state exists precisely when the quote
    // endpoint is unreachable or misbehaving, which is when any claim about
    // what checkout will show is least likely to hold.
    error: "We couldn't estimate freight right now. Please try again shortly.",
  };

  // `invalid_quantity` gets its OWN copy, deliberately. This widget never
  // sends a quantity outside 1-9 (above the maximum it refuses to ask at all
  // -- see quantityOverMaxText), so the status should be unreachable from
  // here -- but if the Worker ever returns it for a reason of its own, telling
  // the shopper to fix their POSTCODE would send them to the wrong field. It
  // never falls through to the generic error either, because the server has
  // told us precisely what is wrong.

  /**
   * The single-carrier state answers to TWO status strings during the
   * transition. The engine side has not settled which name ships first, so
   * both are accepted and render identically; `priced_upper_bound` is the
   * older spelling and can be dropped once the Worker is known to emit only
   * `priced_single_carrier`.
   */
  var SINGLE_CARRIER_STATUSES = ["priced_single_carrier", "priced_upper_bound"];

  /** Every status the endpoint contract may return in a body. */
  var SERVER_STATUSES = [
    "protected",
    "priced",
    "priced_single_carrier",
    "priced_upper_bound",
    "within_allowance",
    "estimate_uncertain",
    "unmeasured",
    "carrier_refused",
    "unavailable",
    "invalid_postcode",
    "invalid_quantity",
  ];

  // Prefixed onto the estimate copy whenever `evidence` resolves to
  // "estimate_similar_items" — a figure interpolated from comparable
  // measured sibling products' actual freight costs, not this item's own
  // carton. Never combined with the exact_quote wording (see resolveEvidence
  // and messageFor).
  var SIMILAR_ITEMS_PREFIX = "Estimated from similar items: ";

  // 2026-09-09 SEVENTEENTH PASS -- the ROUGH ("close enough") tier.
  //
  // `evidence: "estimate_class_typical"` means the figure came from the
  // TYPICAL MEASURED CARTON of this product's (class, product_type) group --
  // a real packed box, belonging to a DIFFERENT product of the same kind --
  // because nothing about this item's own packed size is known. Kyal asked
  // for exactly this rather than "not available yet".
  //
  // The copy therefore does three things and no more: it calls the figure
  // ROUGH, it names what the typical packaging was (the endpoint's
  // `typical_label`, e.g. "digital piano"), and it says out loud that this
  // item's packed size has not been confirmed so the amount may change. It
  // states NO range: the engine publishes no interval for this tier because
  // there is no hold-out to fit one on, and an invented band would read to a
  // shopper exactly like a measured one.
  var TYPICAL_LABEL_FALLBACK = "similar-item";

  /** The kind of item the typical carton came from, as a noun phrase. Any
   * missing/blank/non-string value falls back to "similar-item" -- an older
   * Worker build or a malformed body must never make this sentence claim a
   * category it wasn't told. */
  /** The kind of kit, as a noun phrase ("5-piece acoustic drum kit"), from
   * the same `typical_label` field the rough tier uses. Falls back to "kit"
   * -- never to the rough tier's "similar-item", which would read as a claim
   * about a sibling product's real box. */
  function resolveKitLabel(json) {
    var label = json && typeof json.typical_label === "string" ? json.typical_label.trim() : "";
    return label || "kit";
  }

  function resolveTypicalLabel(json) {
    var label = json && typeof json.typical_label === "string" ? json.typical_label.trim() : "";
    return label || TYPICAL_LABEL_FALLBACK;
  }

  /**
   * The quantity phrase EVERY figure-bearing sentence uses.
   *
   * 2026-09-09: was `pluraliseItems()`, which rendered "1 item" -- so a
   * shopper looking at ONE cabinet read "approximately $130.00 surcharge for
   * 1 item", a quantity-templated phrase leaking into a singular context
   * while every other tier said "for this item". The singular now matches
   * them.
   *
   * 2026-09-10: the plural became "N of this item" (was "N items") and every
   * priced tier now carries it. Two reasons. "2 items" is ambiguous -- it can
   * read as two DIFFERENT items -- while the figure is always N of the ONE
   * product on the page. And the exact-quote/estimate/similar-items/kit
   * sentences said a flat "for this item" while the charge behind them was
   * already multiplied by the quantity, so a shopper buying 3 read a
   * three-item surcharge as a one-item surcharge. The count is part of what
   * the money means; it is now stated wherever money is.
   * @param {number} qty resolved quantity (1..9)
   * @param {string} [noun] the thing being counted; defaults to "item"
   * @returns {string} "this item" | "N of this item" | "this drum kit" | ...
   */
  function ofPhrase(qty, noun) {
    var thing = noun || "item";
    return qty > 1 ? qty + " of this " + thing : "this " + thing;
  }

  /** `ofPhrase` for the ordinary, unlabelled case. */
  function itemsPhrase(qty) {
    return ofPhrase(qty, "item");
  }

  /** The priced/single-carrier rough sentence. `amount` is already formatted
   * ("$123.45"); `qty` is the resolved quantity. Ends in the ONE shared
   * closing sentence, same as every other tier. */
  function typicalPricedText(amount, qty, label) {
    return (
      "Rough freight preview: approximately " +
      amount +
      " surcharge for " +
      itemsPhrase(qty) +
      ", based on typical " +
      label +
      " packaging. Your item's packed size has not been confirmed, so the" +
      " amount may change. " +
      closingSentence()
    );
  }

  // 2026-09-09 TWENTY-THIRD PASS -- the MULTI-BOX (kit consignment) tier.
  //
  // `evidence: "estimate_kit_consignment"` is now the weakest claim the
  // endpoint makes, weaker even than the rough tier above it. The figure is a
  // REAL carrier price for an ASSUMED CARTON SET: the shopper's product is a
  // multi-carton kit, nobody has opened its box, and the number is what it
  // costs to carry the set we GUESS it ships in. So the copy says "assumed
  // carton set" outright rather than borrowing the rough tier's "typical
  // packaging", which implies a real box belonging to a real sibling product.
  //
  // 2026-09-09: it used to close "Preview only — the amount payable is shown
  // at checkout." -- the LIVE promise, written while every other tier still
  // said the charges were not active at checkout. One widget was making two
  // incompatible statements about the same checkout depending on which
  // product the shopper was looking at. It now ends in the ONE shared closing
  // sentence like everything else, and that sentence becomes the
  // amount-payable wording for EVERY tier the moment freight_live flips.
  function kitPricedText(amount, label, qty) {
    return (
      "Rough freight estimate: approximately " +
      amount +
      " for " +
      ofPhrase(qty, label || "kit") +
      ", based on an assumed carton set. Actual packaging is unconfirmed. " +
      closingSentence()
    );
  }

  /** The kit tier's $0. Same shape as the rough tier's, and for the same
   * reason: an unconfirmed carton set cannot settle "no surcharge". Built as
   * a function, not a constant, so it picks up the current closing sentence. */
  function kitWithinAllowanceText() {
    return (
      "This estimate gives no bulky surcharge. It is based on an assumed" +
      " carton set and the actual packaging is unconfirmed, so a surcharge" +
      " may still apply. " +
      closingSentence()
    );
  }

  /** The within-allowance rough sentence. It must NOT read as "no surcharge,
   * settled": the whole point of this tier is that the packed size is
   * unknown, so a surcharge may still turn out to apply. Was a bare
   * " Preview only." -- the shortest and vaguest of the four old closings. */
  function typicalWithinAllowanceText() {
    return (
      "This typical-item calculation gives no bulky surcharge. Your item's" +
      " packed size has not been confirmed, so a surcharge may still apply. " +
      closingSentence()
    );
  }

  // 2026-09-09 NINETEENTH PASS -- the ALLIED-ONLY basis.
  //
  // Kyal: "I wouldn't worry much about TGE - just base it off allied". The
  // engine now prices from a SINGLE enabled carrier, so every sentence that
  // implied a comparison -- the old "cheapest of the ones we checked" framing,
  // and the promise that further quotes might yet reduce or remove the
  // surcharge -- was describing work that no longer happens. Copy that dangles
  // a possible reduction the shopper will never get is worse than no copy at
  // all, so all of it is gone.
  //
  // What replaces it says only what is true: there is one carrier, this is
  // its number, and the number is a preview. The copy STILL NEVER NAMES THE
  // CARRIER ("our carrier's", not "Allied") -- the endpoint does not send a
  // carrier name, so the widget has nothing to name it from, and the basis
  // could be re-tendered without a copy change.
  //
  // Three sentences, one per evidence tier, all ending in the same preview
  // disclaimer (MESSAGES.priced_line2) so the "not active at checkout"
  // promise is written in exactly one place:
  //
  //   exact_quote            -> "...based on our carrier's quote."
  //   estimate (fitted)      -> "...based on our carrier's pricing for this
  //                              item's carton."
  //   estimate_similar_items -> the SIMILAR_ITEMS_PREFIX, then "...based on
  //                              our carrier's pricing." with NO carton
  //                              clause: a sibling-median figure exists
  //                              precisely because this item has no measured
  //                              carton, so claiming one would be false.
  //
  // `estimate_class_typical` keeps its own rough-tier sentence
  // (typicalPricedText) and is handled before any of these.

  /** exact_quote: a real carrier quote for this exact item and postcode. */
  function pricedQuoteText(amount, qty) {
    return (
      "Freight preview: " +
      amount +
      " surcharge for " +
      itemsPhrase(qty) +
      ", based on our carrier's quote. " +
      MESSAGES.priced_line2
    );
  }

  /** estimate: interpolated from the carrier's fitted line for THIS item's
   * own measured carton. */
  function pricedEstimateText(amount, qty) {
    return (
      "Freight preview: estimated " +
      amount +
      " surcharge for " +
      itemsPhrase(qty) +
      ", based on our carrier's pricing for this" +
      " item's carton. " +
      MESSAGES.priced_line2
    );
  }

  /** estimate_similar_items: the median of comparable measured siblings'
   * actual costs. Names the basis WITHOUT a carton claim -- see above. */
  function pricedSimilarItemsText(amount, qty) {
    return (
      SIMILAR_ITEMS_PREFIX +
      "Freight preview: estimated " +
      amount +
      " surcharge for " +
      itemsPhrase(qty) +
      ", based on our carrier's pricing. " +
      MESSAGES.priced_line2
    );
  }

  /**
   * The one place a `priced` / `priced_single_carrier` figure becomes copy.
   * `evidence` has already been resolved; `estimate_class_typical` never
   * reaches here (its callers branch first). An unrecognised value lands on
   * the estimate wording, which promises least.
   * @param {string} amount already formatted, e.g. "$20.50"
   * @param {string} evidence resolved evidence tier
   * @param {number} qty resolved quantity (1..9) the figure covers
   * @returns {string}
   */
  function pricedTextFor(amount, evidence, qty) {
    if (evidence === "exact_quote") return pricedQuoteText(amount, qty);
    if (evidence === "estimate_similar_items") {
      return pricedSimilarItemsText(amount, qty);
    }
    return pricedEstimateText(amount, qty);
  }

  var STATE_CLASS = {
    protected: "ok",
    within_allowance: "ok",
    priced: "neutral",
    priced_single_carrier: "neutral",
    priced_upper_bound: "neutral",
    estimate_uncertain: "neutral",
    unmeasured: "warn",
    carrier_refused: "warn",
    unavailable: "warn",
    invalid_postcode: "warn",
    invalid_quantity: "warn",
    rate_limited: "warn",
    error: "error",
  };

  var REQUEST_TIMEOUT_MS = 6000;
  var QUANTITY_DEBOUNCE_MS = 400;
  var MIN_QUANTITY = 1;
  /** MUST equal the Worker's QUOTE_MAX_QUANTITY (build/worker/src/quote.js).
   * test_calculator.js asserts the two literals match, because a theme that
   * believes the endpoint prices MORE than it does sends requests that 400,
   * and one that believes it prices FEWER refuses quotes it could have got. */
  var MAX_QUANTITY = 9;

  /**
   * The refusal shown when the shopper's selected quantity is above what the
   * endpoint prices. It is NOT a server status: nothing in a response body can
   * produce it, and the widget sends no request at all in this state.
   *
   * 2026-09-10 (Astra review §2): the widget used to CLAMP -- 10 x CDPS110BK
   * asked for 9, and printed "$2,160" beside a cart that would be charged
   * $2,400 at checkout, with no hint a different quantity had been priced. A
   * substituted quantity is an understated price, so the substitution is gone;
   * this sentence takes its place and says where the real figure comes from.
   *
   * 2026-09-10 (checkout decision): the checkout path charges any bulky line
   * above MAX_QUANTITY at the MAX_QUANTITY figure -- a floor, never more -- so
   * the refusal now states that ceiling instead of leaving the shopper to
   * imagine an unbounded charge. Both numbers come from `max`; if MAX_QUANTITY
   * and the Worker's QUOTE_MAX_QUANTITY ever diverge, this promise is wrong,
   * which is why test_calculator.js asserts the two literals match.
   * @param {number} max the endpoint's maximum quantity
   * @returns {string}
   */
  function quantityOverMaxText(max) {
    return (
      "We can only estimate up to " +
      max +
      " of this item here. Larger orders are charged the " +
      max +
      "-unit delivery rate at checkout, never more."
    );
  }

  /** The rendered form of the above, shaped like every other messageFor()
   * return so setResult() handles it identically. */
  function quantityOverMaxMessage() {
    return {
      status: "quantity_over_max",
      state: "warn",
      text: quantityOverMaxText(MAX_QUANTITY),
    };
  }

  /* ------------------------------------------------------------------ *
   * Pure functions (exported)
   * ------------------------------------------------------------------ */

  /**
   * The ONE gate every server-supplied money value passes through before it
   * can become a rendered dollar figure. A surcharge is a whole, non-negative
   * number of cents; anything else is a malformed body, not a price.
   *
   * Rejects: non-numbers (including numeric strings), NaN, Infinity and
   * -Infinity, fractional cents, and negatives. A negative previously rendered
   * as "-$20.50" — a customer-facing figure invented from bad input. It is
   * now invalid input like any other, and its callers fall through to the
   * generic error state.
   *
   * Nothing upstream is expected to send bad data; this is defence in depth,
   * so the widget can never be the thing that shows a shopper a wrong price.
   * @param {*} cents
   * @returns {boolean}
   */
  function isValidCents(cents) {
    if (typeof cents !== "number") return false;
    if (!isFinite(cents)) return false; // NaN, Infinity, -Infinity
    if (Math.floor(cents) !== cents) return false; // fractional cents
    if (cents < 0) return false; // a surcharge is never negative
    return true;
  }

  /**
   * Unit price (integer cents) of the variant the shopper currently has selected.
   * Preferred source is the per-variant map the Liquid snippet emits as
   * `data-variant-prices` ({"<variantId>": <cents>}); when that attribute is
   * absent, malformed, or has no valid entry for this variant, fall back to the
   * single-variant behaviour: the container's `data-price-cents` counts only for
   * the variant the page rendered with. Never throws; undefined means "unknown".
   * @param {{variantPrices: *, currentVariantId: *, initialVariantId: *, initialPrice: *}} src
   * @returns {(number|undefined)}
   */
  function resolveUnitPriceCents(src) {
    var cur = src && src.currentVariantId != null ? String(src.currentVariantId) : "";
    if (cur && src && typeof src.variantPrices === "string" && src.variantPrices) {
      try {
        var map = JSON.parse(src.variantPrices);
        if (map && typeof map === "object" && !Array.isArray(map) &&
            Object.prototype.hasOwnProperty.call(map, cur) &&
            isValidCents(map[cur]) && map[cur] > 0) {
          return map[cur];
        }
      } catch (err) {
        /* malformed map: fall through to the single-variant price */
      }
    }
    var initial = src && src.initialVariantId != null ? String(src.initialVariantId) : "";
    var price = parseInt(src ? src.initialPrice : NaN, 10);
    return initial && initial === cur && !isNaN(price) ? price : undefined;
  }

  /**
   * Render a server-supplied integer number of cents as AUD dollars.
   * 2050 -> "$20.50", 15000 -> "$150.00", 0 -> "$0.00".
   * Returns null for ANY value isValidCents rejects, so a malformed body can
   * never render a bogus price. Every caller must treat null as the generic
   * error state — never as a zero, a blank, or a figure to patch up.
   * @param {*} cents
   * @returns {string|null}
   */
  function formatCents(cents) {
    if (!isValidCents(cents)) return null;
    var dollars = Math.floor(cents / 100);
    var remainder = cents % 100;
    var padded = remainder < 10 ? "0" + remainder : String(remainder);
    return "$" + dollars + "." + padded;
  }

  /**
   * Normalise a typed postcode to exactly 4 digits, PRESERVING a leading zero
   * ("0800" stays "0800"). Anything that is not exactly 4 digits after
   * trimming — including "800", "12345", "3l99", "" and null — is invalid and
   * returns null. Short entries are deliberately NOT zero-padded: the button
   * stays disabled until 4 digits are present, so padding would silently
   * price a postcode the shopper never typed.
   * @param {*} raw
   * @returns {string|null}
   */
  function normalisePostcode(raw) {
    if (raw == null) return null;
    var trimmed = String(raw).trim();
    return /^[0-9]{4}$/.test(trimmed) ? trimmed : null;
  }

  /**
   * The shopper's SELECTED quantity as a whole number >= 1, with NO upper
   * clamp. Anything unparseable, fractional, or below 1 falls back to 1;
   * everything else passes through, however large.
   *
   * 2026-09-10: this is the value the request path uses, and the absence of an
   * upper clamp is the entire point -- a selection above MAX_QUANTITY has to
   * stay visible so the caller can REFUSE rather than silently price a
   * smaller order (Astra review §2).
   * @param {*} raw
   * @returns {number} >= 1
   */
  function resolveSelectedQuantity(raw) {
    if (typeof raw === "number") {
      if (!isFinite(raw)) return MIN_QUANTITY;
    } else {
      if (raw == null) return MIN_QUANTITY;
      var trimmed = String(raw).trim();
      if (!/^[+-]?[0-9]+$/.test(trimmed)) return MIN_QUANTITY;
      raw = parseInt(trimmed, 10);
      if (isNaN(raw)) return MIN_QUANTITY;
    }
    var n = Math.floor(raw);
    return n < MIN_QUANTITY ? MIN_QUANTITY : n;
  }

  /**
   * Resolve a quantity to a whole number within 1..9. Used for the quantity
   * the RESPONSE echoes back -- a value the endpoint has already validated to
   * 1..9, so the upper clamp is only a guard against a malformed body
   * producing "for 4,000,000 of this item" in customer-facing copy. It is NOT
   * used to decide what to request: see resolveSelectedQuantity().
   * @param {*} raw
   * @returns {number} 1..9
   */
  function resolveQuantity(raw) {
    var n = resolveSelectedQuantity(raw);
    return n > MAX_QUANTITY ? MAX_QUANTITY : n;
  }

  /**
   * How strong the evidence behind a priced figure is: `"exact_quote"` (a
   * real carrier quote for this exact product at this exact postcode),
   * `"estimate_similar_items"` (this SKU has no measured carton of its own,
   * so the figure is the median of comparable measured sibling products'
   * actual freight costs at this postcode), or `"estimate"` (anything else --
   * today, a figure interpolated from that carrier's fitted line for this
   * item's carton family).
   *
   * **A response that does not say, or says something unrecognised, gets
   * `"estimate"`.** Only an explicit `evidence: "exact_quote"` or
   * `evidence: "estimate_similar_items"` earns its own wording, so an older
   * Worker build, a cached response, or a malformed body can never make this
   * widget promise more than it should. Added 2026-09-08 twelfth pass with
   * the `evidence` field on the endpoint; `"estimate_similar_items"` added
   * 2026-09-09.
   *
   * @param {Object} json parsed response body
   * @returns {string} "exact_quote" | "estimate_similar_items" | "estimate"
   */
  function resolveEvidence(json) {
    if (json && json.evidence === "exact_quote") return "exact_quote";
    if (json && json.evidence === "estimate_similar_items") {
      return "estimate_similar_items";
    }
    // 2026-09-09 seventeenth pass -- the ROUGH tier, the weakest claim of
    // the four. Same doctrine as the two above: only an EXPLICIT match earns
    // its own wording, so an older Worker build or a malformed body falls
    // back to "estimate", which promises least.
    if (json && json.evidence === "estimate_class_typical") {
      return "estimate_class_typical";
    }
    // 2026-09-09 twenty-third pass -- the MULTI-BOX tier, weakest of the five.
    if (json && json.evidence === "estimate_kit_consignment") {
      return "estimate_kit_consignment";
    }
    return "estimate";
  }

  /**
   * Map a settled request outcome to the exact customer-facing message.
   *
   * @param {Object} outcome
   * @param {boolean} outcome.ok        true only for a 2xx response
   * @param {number}  [outcome.httpStatus]
   * @param {Object}  [outcome.json]    parsed body, if any
   * @param {boolean} [outcome.failed]  network failure / timeout / bad JSON
   * @returns {{ text: string, state: string, status: string }}
   *   `text` is the literal copy (the `priced` case is two lines joined by a
   *   newline); `state` is the CSS modifier bucket; `status` is the resolved
   *   logical status ("error" / "rate_limited" for the non-body cases).
   */
  function standardMessageFor(outcome) {
    outcome = outcome || {};
    if (outcome.failed) return build("error");
    if (outcome.httpStatus === 429) return build("rate_limited");

    var json = outcome.json;
    var status =
      json && typeof json.status === "string" ? json.status : null;

    // A non-2xx response is only allowed to speak for itself when its body
    // carries one of the two "your input was wrong" statuses; every other
    // non-2xx is the generic error regardless of what the body claims, so a
    // 500 can never render a price.
    if (!outcome.ok) {
      if (status === "invalid_postcode" || status === "invalid_quantity") {
        return build(status);
      }
      return build("error");
    }

    // 2026-09-08 twelfth pass: the wording is driven by `evidence`, not by
    // an assumption about WHICH carrier produced the figure. The old copy
    // said "using the Allied quote for this item" -- an attribution error
    // once a figure can come from TGE, or from a fitted line rather than a
    // quote at all. See resolveEvidence().
    var evidence = resolveEvidence(json);

    // `priced_single_carrier` should no longer occur at all now the engine
    // prices from ONE enabled carrier -- those lines come back as `priced`
    // with carrier_count 1. The status is still handled, defensively, in case
    // an older Worker build or a re-tender brings it back; since the copy no
    // longer distinguishes "one carrier" from "the cheapest of several", it
    // now renders EXACTLY the same sentences as `priced`.
    if (SINGLE_CARRIER_STATUSES.indexOf(status) !== -1) {
      var boundAmount = formatCents(json.charge_cents);
      var boundQty = resolveQuantity(json.quantity);
      if (boundAmount === null) return build("error");
      if (evidence === "estimate_class_typical") {
        // The rough tier owns this branch outright: a figure from ANOTHER
        // product's carton must not be worded as "our carrier's pricing for
        // this item's carton", which is a claim about a carton nobody has
        // measured. Same sentence as the `priced` case.
        return {
          status: status,
          state: STATE_CLASS[status],
          text: typicalPricedText(boundAmount, boundQty, resolveTypicalLabel(json)),
        };
      }
      if (evidence === "estimate_kit_consignment") {
        return {
          status: status,
          state: STATE_CLASS[status],
          text: kitPricedText(boundAmount, resolveKitLabel(json), boundQty),
        };
      }
      return {
        status: status,
        state: STATE_CLASS[status],
        text: pricedTextFor(boundAmount, evidence, boundQty),
      };
    }

    if (status === "priced") {
      var amount = formatCents(json.charge_cents);
      var qty = resolveQuantity(json.quantity);
      if (amount === null) return build("error");
      // An ESTIMATE must not be worded like a quote -- it is a modelled
      // figure, and the copy says so. The rough tier branches first; every
      // other tier goes through the one shared builder, so `priced` and
      // `priced_single_carrier` can never drift apart.
      if (evidence === "estimate_class_typical") {
        return {
          status: "priced",
          state: STATE_CLASS.priced,
          text: typicalPricedText(amount, qty, resolveTypicalLabel(json)),
        };
      }
      if (evidence === "estimate_kit_consignment") {
        return {
          status: "priced",
          state: STATE_CLASS.priced,
          text: kitPricedText(amount, resolveKitLabel(json), qty),
        };
      }
      // exact_quote always wins over any other evidence value a malformed
      // body might also carry, so the similar-items prefix can only ever land
      // on the estimate wording, never alongside the exact-quote sentence.
      return {
        status: "priced",
        state: STATE_CLASS.priced,
        text: pricedTextFor(amount, evidence, qty),
      };
    }

    if (status === "within_allowance") {
      // Same evidence-driven prefix as the priced branches above; exact_quote
      // can never reach here alongside estimate_similar_items since evidence
      // resolves to a single value from one `evidence` field.
      // The rough tier's $0 is NOT the ordinary "within our allowance"
      // statement: that one is about a freight figure we stand behind, and
      // this one is about a box nobody has measured. It says so, and it names
      // no allowance at all -- there is nothing settled to compare against.
      if (evidence === "estimate_class_typical") {
        return {
          status: "within_allowance",
          state: STATE_CLASS.within_allowance,
          text: typicalWithinAllowanceText(),
        };
      }
      if (evidence === "estimate_kit_consignment") {
        return {
          status: "within_allowance",
          state: STATE_CLASS.within_allowance,
          text: kitWithinAllowanceText(),
        };
      }
      // The allowance figure comes from the RESPONSE, never from this file.
      var withinText = withinAllowanceTextFor(json);
      return {
        status: "within_allowance",
        state: STATE_CLASS.within_allowance,
        text:
          evidence === "estimate_similar_items"
            ? SIMILAR_ITEMS_PREFIX + withinText
            : withinText,
      };
    }

    // Handled before the generic allow-list fallthrough because it needs
    // formatted values, not fixed copy. A straddling p25..p75 band means we
    // genuinely cannot say which side of the allowance this item lands
    // on, so the copy states a range rather than a single figure -- and it
    // must never gain the `priced_line2` checkout line, since it is not a
    // committed figure at all.
    if (status === "estimate_uncertain") {
      var rangeLowRaw = formatCents(json.range_low_cents);
      var rangeHigh = formatCents(json.range_high_cents);
      // BOTH bounds must be valid cents. A half-present band is a malformed
      // body, not a narrower answer, and guessing the missing half would
      // invent the one number this status exists to avoid inventing.
      // A response with no usable band at all is an OLDER ENGINE that knows
      // the status but not the interval. It is not the generic error -- the
      // server told us precisely what kind of gap this is -- and it is not
      // `unmeasured` either, because we do know something: that comparable
      // items vary across the threshold. So it gets the interim wording,
      // which states exactly that and invents no figures.
      if (rangeHigh === null || rangeLowRaw === null) {
        return {
          status: "estimate_uncertain",
          state: STATE_CLASS.estimate_uncertain,
          text: MESSAGES.estimate_uncertain_no_range,
        };
      }
      // The two bounds are CHARGES, so the low one is $0 whenever the
      // interval's low end sits inside the allowance -- the common
      // case, and the one the copy has to say out loud: "up to $HIGH" would
      // read as a ceiling the shopper is committed to, when the honest
      // answer is that the surcharge may be nothing at all.
      var amountPhrase =
        json.range_low_cents === 0
          ? "could be $0 or approximately " + rangeHigh
          : "could be approximately " + rangeLowRaw + "–" + rangeHigh;
      return {
        status: "estimate_uncertain",
        state: STATE_CLASS.estimate_uncertain,
        text:
          "Freight to this postcode may attract a bulky-item surcharge for " +
          // The band is a CHARGE band, so it is multiplied by quantity just
          // like every other figure: the count belongs in the sentence.
          itemsPhrase(resolveQuantity(json.quantity)) +
          ": the surcharge " +
          amountPhrase +
          ". " +
          MESSAGES.estimate_uncertain_preview_note,
      };
    }

    // 2026-09-09 -- `unmeasured` is the one fixed-copy status whose sentence
    // depends on the body, so it is built here rather than through build().
    // See unmeasuredText(): a product with a MEASURED carton that merely has
    // no usable line at this destination says so ("for this postcode"),
    // instead of telling the shopper we know nothing about a product we can
    // price at other postcodes.
    if (status === "unmeasured") {
      return {
        status: "unmeasured",
        state: STATE_CLASS.unmeasured,
        text: unmeasuredText(resolveCarton(json)),
      };
    }

    // Explicit allow-list: only a contract status may select copy. A body
    // echoing an internal key (e.g. "priced_line2") must NOT reach a message.
    if (status && SERVER_STATUSES.indexOf(status) !== -1) return build(status);
    return build("error");

    function build(key) {
      return {
        status: key,
        state: STATE_CLASS[key] || "error",
        text: MESSAGES[key],
      };
    }
  }

  /**
   * The Express line for a settled Standard message, or null when there is
   * no Standard figure to build it from (unmeasured, error, invalid input,
   * carrier_refused, unavailable, quantity over max, rate limit).
   *
   * @param {{status: string}} msg  the Standard message (standardMessageFor)
   * @param {Object} json           parsed response body
   * @param {{priceCents: (number|undefined), thresholdCents: (number|undefined)}} [ctx]
   *   the product's unit price and the free-shipping threshold, used ONLY for
   *   the ordinary-flat-rate cases (protected / within_allowance).
   * @returns {string|null}
   */
  function expressFor(msg, json, ctx) {
    if (!msg || !json) return null;
    var status = msg.status;
    var evidence = resolveEvidence(json);
    var priced = status === "priced" || SINGLE_CARRIER_STATUSES.indexOf(status) !== -1;
    var hedgedCarton =
      evidence === "estimate_class_typical" || evidence === "estimate_kit_consignment";

    if (priced) {
      var base = json.charge_cents;
      if (!isValidCents(base)) return null;
      var word = "";
      if (hedgedCarton) {
        word = "approximately ";
      } else if (evidence !== "exact_quote") {
        word = "estimated "; // estimate and estimate_similar_items
      }
      return "Express: " + word + formatCents(base + EXPRESS_UPLIFT_CENTS);
    }

    if (status === "estimate_uncertain") {
      return "Express: " + formatCents(EXPRESS_UPLIFT_CENTS) + " more than Standard.";
    }

    if (status === "protected" || status === "within_allowance") {
      // When the carton is unconfirmed the Standard line is hedged ("a
      // surcharge may still apply"); Express then carries the hedge in the
      // only form that stays true.
      if (hedgedCarton) {
        return "Express: " + formatCents(EXPRESS_UPLIFT_CENTS) + " more than Standard.";
      }
      var word2 =
        status === "within_allowance" && evidence === "estimate_similar_items" ? "estimated " : "";
      var threshold =
        ctx && isValidCents(ctx.thresholdCents) && ctx.thresholdCents > 0
          ? ctx.thresholdCents
          : FREE_THRESHOLD_FALLBACK_CENTS;
      var thresholdLabel = formatAllowance(threshold);
      var free = formatCents(EXPRESS_UPLIFT_CENTS);
      var paid = formatCents(STANDARD_FLAT_CENTS + EXPRESS_UPLIFT_CENTS);
      if (ctx && isValidCents(ctx.priceCents) && ctx.priceCents > 0) {
        var total = ctx.priceCents * resolveQuantity(json.quantity);
        if (total >= threshold) return "Express: " + word2 + free;
        return (
          "Express: " + word2 + paid + " (or " + free + " on orders of " + thresholdLabel + " or more)"
        );
      }
      return (
        "Express: " + word2 + free + " on orders of " + thresholdLabel + " or more, otherwise " + paid
      );
    }
    return null;
  }

  /**
   * Split an Express line ("Express: estimated $165.00", "Express: $22.00 (or
   * $15.00 on orders of $150 or more)") into display parts. Pure presentation:
   * the words and figures are copied, never computed. Returns null when the
   * line has an unexpected shape, and the caller falls back to plain text.
   */
  function splitExpress(line) {
    var m = /^Express: (estimated |approximately )?(\$\d+\.\d\d)(?: ?([\s\S]*))?$/.exec(String(line));
    if (!m) return null;
    return { hedge: m[1] ? m[1].replace(/ $/, "") : "", amount: m[2], tail: m[3] || "" };
  }

  /**
   * messageFor = the unchanged Standard message, plus (when a Standard figure
   * exists) `express` (the Express line) and `expressNote` (the one-off
   * "bulky items only" sentence). `text` is byte-for-byte what it was before.
   * @param {Object} outcome  see standardMessageFor
   * @param {Object} [ctx]    see expressFor
   */
  function messageFor(outcome, ctx) {
    var msg = standardMessageFor(outcome);
    var express = null;
    if (outcome && outcome.ok && outcome.json) {
      try {
        express = expressFor(msg, outcome.json, ctx);
      } catch (err) {
        express = null;
      }
    }
    if (express) {
      msg.express = express;
      msg.expressNote = EXPRESS_NOTE;
    }
    return msg;
  }

  /**
   * Trailing-edge debounce. Returns a wrapper with a .cancel() so a pending
   * quantity trigger can be dropped when the block is hidden by a variant swap.
   * @param {Function} fn
   * @param {number} wait milliseconds
   * @returns {Function & { cancel: Function }}
   */
  function debounce(fn, wait) {
    var timer = null;
    function wrapped() {
      var args = arguments;
      var self = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(self, args);
      }, wait);
    }
    wrapped.cancel = function () {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    return wrapped;
  }

  /**
   * Build the quote URL. Kept pure so the test page can assert on it.
   * @param {string} base
   * @param {{sku: string, postcode: string, quantity: number, priceCents?: number}} params
   *   priceCents (unit price, integer cents) is appended as `&price=` ONLY when it is
   *   a positive integer; the Worker uses it for the full-freight-under-$500 rule and
   *   an older Worker rejects unknown params, so never send it speculatively.
   * @returns {string}
   */
  function buildQuoteUrl(base, params) {
    var url =
      String(base) +
      "?sku=" +
      encodeURIComponent(params.sku) +
      "&postcode=" +
      encodeURIComponent(params.postcode) +
      "&quantity=" +
      encodeURIComponent(String(params.quantity));
    var price = params.priceCents;
    if (typeof price === "number" && isFinite(price) && price > 0 && Math.floor(price) === price) {
      url += "&price=" + String(price);
    }
    return url;
  }

  /**
   * COMPACT-FIRST DISCLOSURE (2026-09-09, Kyal-approved).
   *
   * Binds the one compact line (`toggle`, a real <button type="button">) to
   * the panel that holds the whole calculator. Kept out of initNotice and
   * exported so build/theme/test_calculator.js can drive it with plain element
   * stubs -- it touches nothing but attributes, `hidden` and focus.
   *
   * Contract:
   *   - It ALWAYS starts collapsed. Nothing auto-expands, and no previous
   *     state is read from anywhere: there is no cookie, no localStorage, no
   *     sessionStorage, no geo lookup. A metro shopper who never taps the line
   *     sees one line and nothing else.
   *   - Visibility is the `hidden` ATTRIBUTE (the theme reset has
   *     `[hidden]{display:none!important}`), never an inline style, so the
   *     panel is genuinely removed from the accessibility tree while closed.
   *   - `aria-expanded` on the toggle and `hidden` on the panel move together;
   *     they can never disagree because only setExpanded writes either.
   *   - Focus moves to the postcode field on EXPAND only. Collapsing does not
   *     steal focus (the shopper may be tabbing past).
   *   - Enter and Space are NOT handled here. `toggle` is a real <button>, so
   *     the browser turns both into a click; a hand-rolled key handler would
   *     race that and toggle twice.
   *   - The toggle's LABEL is never rewritten. When a result is already shown
   *     and the shopper collapses the panel, the compact line still reads
   *     "Regional or remote delivery? Check freight" -- summarising a proposed,
   *     not-active-at-checkout figure into a permanently visible line is a
   *     claim this widget must not make.
   *
   * @param {Element} toggle the compact-line button
   * @param {Element} panel  the element it controls
   * @param {Element} [focusTarget] focused on expand, when present
   * @returns {{expand: Function, collapse: Function, toggle: Function,
   *            isExpanded: Function}|null} null when either element is missing
   */
  function wireDisclosure(toggle, panel, focusTarget) {
    if (!toggle || !panel) return null;
    var expanded = false;

    function setExpanded(next, moveFocus) {
      expanded = !!next;
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      panel.hidden = !expanded;
      if (expanded && moveFocus && focusTarget && typeof focusTarget.focus === "function") {
        try {
          focusTarget.focus();
        } catch (err) {
          /* a detached or unfocusable field must never break the toggle */
        }
      }
    }

    // Normalise to collapsed regardless of what the server-rendered markup
    // said, so a stale cached page can never open by itself.
    setExpanded(false, false);

    if (toggle.addEventListener) {
      toggle.addEventListener("click", function (evt) {
        if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
        setExpanded(!expanded, true);
      });
    }

    return {
      expand: function () {
        setExpanded(true, true);
      },
      collapse: function () {
        setExpanded(false, false);
      },
      toggle: function () {
        setExpanded(!expanded, true);
      },
      isExpanded: function () {
        return expanded;
      },
    };
  }

  var API = {
    MESSAGES: MESSAGES,
    SERVER_STATUSES: SERVER_STATUSES,
    SINGLE_CARRIER_STATUSES: SINGLE_CARRIER_STATUSES,
    STATE_CLASS: STATE_CLASS,
    REQUEST_TIMEOUT_MS: REQUEST_TIMEOUT_MS,
    QUANTITY_DEBOUNCE_MS: QUANTITY_DEBOUNCE_MS,
    ALLOWANCE_FALLBACK_LABEL: ALLOWANCE_FALLBACK_LABEL,
    isValidCents: isValidCents,
    resolveUnitPriceCents: resolveUnitPriceCents,
    resolveEvidence: resolveEvidence,
    resolveCarton: resolveCarton,
    unmeasuredText: unmeasuredText,
    closingSentence: closingSentence,
    closingSentencePlural: closingSentencePlural,
    setFreightLive: setFreightLive,
    CLOSING_PREVIEW: CLOSING_PREVIEW,
    CLOSING_LIVE: CLOSING_LIVE,
    CLOSING_PREVIEW_PLURAL: CLOSING_PREVIEW_PLURAL,
    formatCents: formatCents,
    formatAllowance: formatAllowance,
    withinAllowanceText: withinAllowanceText,
    normalisePostcode: normalisePostcode,
    resolveQuantity: resolveQuantity,
    resolveSelectedQuantity: resolveSelectedQuantity,
    MIN_QUANTITY: MIN_QUANTITY,
    MAX_QUANTITY: MAX_QUANTITY,
    quantityOverMaxText: quantityOverMaxText,
    quantityOverMaxMessage: quantityOverMaxMessage,
    messageFor: messageFor,
    standardMessageFor: standardMessageFor,
    expressFor: expressFor,
    splitExpress: splitExpress,
    EXPRESS_UPLIFT_CENTS: EXPRESS_UPLIFT_CENTS,
    STANDARD_FLAT_CENTS: STANDARD_FLAT_CENTS,
    EXPRESS_NOTE: EXPRESS_NOTE,
    debounce: debounce,
    wireDisclosure: wireDisclosure,
    buildQuoteUrl: buildQuoteUrl,
  };

  if (root) root.ScarlettFreight = API;
  if (typeof module !== "undefined" && module.exports) module.exports = API;

  /* ------------------------------------------------------------------ *
   * DOM wiring — browser only
   * ------------------------------------------------------------------ */

  if (typeof document === "undefined") return;

  // Asset URLs come from the container's data-classes-url attribute (set in
  // Liquid via the `asset_url` filter). A theme asset is NEVER reachable at a
  // guessed "/assets/<file>" path on the storefront domain (that 404s) — it
  // only exists at its versioned CDN URL, which only Liquid knows, so this
  // script must be handed it rather than construct it.
  var classesPromiseByUrl = {};

  function fetchJson(url) {
    return fetch(url, { credentials: "omit" }).then(function (res) {
      if (!res.ok) throw new Error(url + " HTTP " + res.status);
      return res.json();
    });
  }

  function loadClasses(url) {
    if (!url) return Promise.resolve({});
    if (!classesPromiseByUrl[url]) {
      classesPromiseByUrl[url] = fetchJson(url).catch(function (err) {
        console.warn("[scarlett-freight] could not load freight classes:", err);
        return {};
      });
    }
    return classesPromiseByUrl[url];
  }

  function readVariantSkuMap(container) {
    var productId = container.getAttribute("data-product-id");
    var script =
      (productId &&
        document.getElementById("ScarlettFreightVariantSkus-" + productId)) ||
      container.querySelector("script[data-scarlett-freight-variant-skus]");
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || "{}") || {};
    } catch (err) {
      console.warn("[scarlett-freight] malformed variant/SKU map:", err);
      return {};
    }
  }

  /**
   * Find the theme's own quantity input, defensively. Dawn and Horizon both
   * ship `input[name="quantity"]` inside a <quantity-input> wrapper in the
   * product form, but section overrides and older ports vary, so this walks a
   * list of candidate selectors (nearest product form first, then the page)
   * and takes the first VISIBLE match. Returns null when nothing looks like a
   * quantity input, in which case quantity resolves to 1.
   * @param {Element} container
   * @returns {HTMLInputElement|null}
   */
  function findQuantityInput(container) {
    var selectors = [
      'input[name="quantity"]',
      "input[data-quantity-input]",
      "quantity-input input",
      '[data-quantity-input] input[type="number"]',
      '.quantity input[type="number"]',
    ];
    var scopes = [];
    var form =
      container.closest && container.closest("form[action*='/cart/add']");
    if (form) scopes.push(form);
    var productForm = document.querySelector("form[action*='/cart/add']");
    if (productForm && scopes.indexOf(productForm) === -1) {
      scopes.push(productForm);
    }
    scopes.push(document);

    for (var s = 0; s < scopes.length; s++) {
      for (var i = 0; i < selectors.length; i++) {
        var found;
        try {
          found = scopes[s].querySelectorAll(selectors[i]);
        } catch (err) {
          continue;
        }
        for (var j = 0; j < found.length; j++) {
          var el = found[j];
          // Skip our own postcode field and anything hidden from the shopper.
          if (el.name === "postcode") continue;
          if (container.contains(el)) continue;
          if (el.type === "hidden") continue;
          if (el.offsetParent === null && el.type !== "number") continue;
          return el;
        }
      }
    }
    return null;
  }

  function initNotice(container) {
    // The go-live copy switch, read before anything renders. The Liquid
    // assign in snippets/scarlett-freight-notice.liquid is the ONE place it
    // is set; only the exact string "true" flips it, so a missing or
    // mistyped attribute keeps the preview wording.
    setFreightLive(container.getAttribute("data-freight-live") === "true");

    var variantSkus = readVariantSkuMap(container);
    var classesUrl = container.getAttribute("data-classes-url");
    var endpoint = container.getAttribute("data-quote-endpoint");
    var currentVariantId =
      container.getAttribute("data-initial-variant-id") || "";
    var form = container.querySelector("[data-scarlett-freight-form]");
    var input = form ? form.querySelector('input[name="postcode"]') : null;
    var button = form ? form.querySelector("button[type='submit']") : null;
    var resultEl = container.querySelector(".scarlett-freight-notice__result");
    var buttonIdleLabel = button ? button.textContent : "Check freight";

    // Compact-first: the block shows ONE line until the shopper asks for the
    // calculator. See wireDisclosure() for the full contract.
    var disclosure = wireDisclosure(
      container.querySelector("[data-scarlett-freight-toggle]"),
      container.querySelector("[data-scarlett-freight-panel]"),
      input
    );

    var currentSku = null;
    var lastPostcode = null; // set only after a successful submit
    var lastResultStatus = null; // what the result line is currently showing
    var controller = null; // in-flight AbortController
    var timeoutId = null;
    var requestToken = 0; // guards against a stale response rendering

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      n.className = cls;
      if (text) n.textContent = text;
      return n;
    }

    function buildExpressCard(line, note) {
      var parts = splitExpress(line);
      var card = el("span", "scarlett-freight-notice__express");
      var head = el("span", "scarlett-freight-notice__express-head");
      var svgNs = "http://www.w3.org/2000/svg";
      var svg = document.createElementNS(svgNs, "svg");
      svg.setAttribute("width", "16");
      svg.setAttribute("height", "16");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("fill", "none");
      svg.setAttribute("stroke", "currentColor");
      svg.setAttribute("stroke-width", "2");
      svg.setAttribute("stroke-linecap", "round");
      svg.setAttribute("stroke-linejoin", "round");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      var c = document.createElementNS(svgNs, "circle");
      c.setAttribute("cx", "12"); c.setAttribute("cy", "12"); c.setAttribute("r", "9");
      var pth = document.createElementNS(svgNs, "path");
      pth.setAttribute("d", "M12 7v5l3 2");
      svg.appendChild(c); svg.appendChild(pth);
      var badge = el("span", "scarlett-freight-notice__express-badge");
      badge.appendChild(svg);
      badge.appendChild(document.createTextNode("Express"));
      head.appendChild(badge);
      card.appendChild(head);
      var body = el("span", "scarlett-freight-notice__express-body");
      if (parts) {
        if (parts.hedge) body.appendChild(el("span", "scarlett-freight-notice__express-hedge", parts.hedge + " "));
        body.appendChild(el("span", "scarlett-freight-notice__express-amount", parts.amount));
        if (parts.tail) body.appendChild(el("span", "scarlett-freight-notice__express-tail", " " + parts.tail));
      } else {
        body.appendChild(el("span", "scarlett-freight-notice__express-tail", line));
      }
      card.appendChild(body);
      if (note) card.appendChild(el("span", "scarlett-freight-notice__express-note", note));
      return card;
    }

    function setResult(outcome) {
      lastResultStatus = outcome ? outcome.status : null;
      if (!resultEl) return;
      resultEl.textContent = "";
      resultEl.className = "scarlett-freight-notice__result";
      if (!outcome) return;
      resultEl.classList.add(
        "scarlett-freight-notice__result--" + outcome.state
      );
      var lines = String(outcome.text).split("\n");
      for (var i = 0; i < lines.length; i++) {
        var line = document.createElement("span");
        line.className = "scarlett-freight-notice__line";
        if (i === 0) line.classList.add("scarlett-freight-notice__line--lead");
        line.textContent = lines[i];
        resultEl.appendChild(line);
      }
      if (outcome.express) {
        resultEl.appendChild(buildExpressCard(outcome.express, outcome.expressNote));
      }
    }

    function setLoading(isLoading) {
      container.classList.toggle(
        "scarlett-freight-notice--loading",
        !!isLoading
      );
      if (!button) return;
      if (isLoading) {
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        button.textContent = "Checking…";
      } else {
        button.removeAttribute("aria-busy");
        button.textContent = buttonIdleLabel;
        syncButtonEnabled();
      }
    }

    /**
     * The submit button is enabled as soon as the field has ANY content --
     * NOT only at exactly 4 digits (2026-09-09).
     *
     * It used to stay disabled until the postcode normalised, which meant a
     * shopper who typed "99" could not submit at all, and the widget said
     * nothing. On desktop the browser's own constraint bubble papered over
     * it; on mobile that bubble is never drawn, so a partial postcode showed
     * EMPTY -- no result, no error, a dead button. The form now carries
     * `novalidate` (see the snippet) and this widget states the problem in
     * its own words, at both widths, through handleSubmit's
     * `invalid_postcode` message.
     *
     * An EMPTY field still disables the button: there is nothing to correct
     * yet, and shouting at a shopper who has not typed anything is worse
     * than an inert button they can see is inert.
     */
    function syncButtonEnabled() {
      if (!button) return;
      if (button.getAttribute("aria-busy") === "true") return;
      var raw = input ? String(input.value == null ? "" : input.value).trim() : "";
      button.disabled = raw.length === 0;
    }

    /** Show Scarlett's own "Enter a 4-digit Australian postcode." line. */
    function showInvalidPostcode() {
      setResult(messageFor({ ok: true, json: { status: "invalid_postcode" } }));
    }

    /** The quantity the shopper actually selected, UNCLAMPED -- requestQuote
     * needs to see a 10 as a 10 so it can refuse instead of pricing 9. */
    function currentQuantity() {
      var qtyEl = findQuantityInput(container);
      return resolveSelectedQuantity(qtyEl ? qtyEl.value : null);
    }

    /** Unit price + free-shipping threshold for the quote and the ordinary-flat-rate
     * Express line. The price follows the selected variant via the container's
     * data-variant-prices map; with no usable entry it is withheld for any variant
     * other than the one the page rendered with (see resolveUnitPriceCents). */
    function expressContext() {
      var thr = parseInt(container.getAttribute("data-free-threshold-cents"), 10);
      return {
        priceCents: resolveUnitPriceCents({
          variantPrices: container.getAttribute("data-variant-prices"),
          currentVariantId: currentVariantId,
          initialVariantId: container.getAttribute("data-initial-variant-id"),
          initialPrice: container.getAttribute("data-price-cents"),
        }),
        thresholdCents: isNaN(thr) ? undefined : thr,
      };
    }

    function clearInFlight() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (controller) {
        try {
          controller.abort();
        } catch (err) {
          /* AbortController unsupported or already settled — harmless */
        }
        controller = null;
      }
    }

    /**
     * Fire a quote request for the current sku/postcode/quantity.
     * In-flight policy: ABORT-AND-RESTART. A newer trigger always wins, and
     * the token check means an aborted-but-still-resolving older request can
     * never paint over the newer one's state.
     */
    function requestQuote() {
      if (!currentSku || !lastPostcode || !endpoint) return;
      var quantity = currentQuantity();

      // Above the endpoint's maximum the widget asks for NOTHING. The token
      // is still advanced so an older in-flight response cannot paint a price
      // over this refusal, and the loading state is cleared because no
      // request is coming back to clear it.
      if (quantity > MAX_QUANTITY) {
        requestToken++;
        clearInFlight();
        setLoading(false);
        setResult(quantityOverMaxMessage());
        return;
      }

      var token = ++requestToken;

      clearInFlight();
      setLoading(true);

      var signal;
      if (typeof AbortController !== "undefined") {
        controller = new AbortController();
        signal = controller.signal;
        timeoutId = setTimeout(function () {
          timeoutId = null;
          try {
            controller.abort();
          } catch (err) {
            /* already settled */
          }
        }, REQUEST_TIMEOUT_MS);
      } else {
        // No AbortController (very old browser): still bound the wait so the
        // loading state cannot stick forever.
        timeoutId = setTimeout(function () {
          timeoutId = null;
          if (token === requestToken) {
            setLoading(false);
            setResult(messageFor({ failed: true }));
          }
        }, REQUEST_TIMEOUT_MS);
      }

      var url = buildQuoteUrl(endpoint, {
        sku: currentSku,
        postcode: lastPostcode,
        quantity: quantity,
        priceCents: expressContext().priceCents,
      });

      var opts = { credentials: "omit", headers: { Accept: "application/json" } };
      if (signal) opts.signal = signal;

      fetch(url, opts).then(
        function (res) {
          return res
            .json()
            .catch(function () {
              return null;
            })
            .then(function (json) {
              settle(token, {
                ok: res.ok,
                httpStatus: res.status,
                json: json,
              });
            });
        },
        function () {
          // Network failure, timeout-abort, or a newer request aborting this
          // one. Either way the token check below decides whether it renders.
          settle(token, { failed: true });
        }
      );
    }

    function settle(token, outcome) {
      if (token !== requestToken) return; // a fresher request owns the UI
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      controller = null;
      setLoading(false);
      try {
        setResult(messageFor(outcome, expressContext()));
      } catch (err) {
        console.warn("[scarlett-freight] render failed:", err);
        setResult({
          state: "error",
          status: "error",
          text: MESSAGES.error,
        });
      }
    }

    var requestQuoteDebounced = debounce(requestQuote, QUANTITY_DEBOUNCE_MS);

    function applyClassForCurrentVariant() {
      var sku = variantSkus[currentVariantId];
      if (!sku) {
        hideBlock();
        return;
      }
      loadClasses(classesUrl).then(function (classes) {
        var cls = classes && classes[sku];
        if (cls === "C" || cls === "D" || cls === "E") {
          currentSku = sku;
          container.hidden = false;
          syncButtonEnabled();
          // Re-price the new variant only if the shopper already told us where
          // they are; otherwise just clear the stale result.
          if (lastPostcode) {
            requestQuoteDebounced.cancel();
            requestQuote();
          } else {
            setResult(null);
          }
        } else {
          hideBlock();
        }
      });
    }

    function hideBlock() {
      currentSku = null;
      container.hidden = true;
      // The whole block is going away (a variant with no bulky class), so the
      // panel must not still be open underneath it if a later variant swap
      // brings the block back -- it re-appears as the compact line, as on a
      // fresh load. Collapsing never moves focus.
      if (disclosure) disclosure.collapse();
      requestQuoteDebounced.cancel();
      clearInFlight();
      requestToken++; // orphan any in-flight response
      setLoading(false);
      setResult(null);
    }

    function handleSubmit(evt) {
      evt.preventDefault();
      if (!currentSku) return;
      var pc = normalisePostcode(input ? input.value : "");
      if (pc === null) {
        showInvalidPostcode();
        return;
      }
      lastPostcode = pc;
      requestQuoteDebounced.cancel();
      requestQuote();
    }

    if (form) {
      // Belt and braces alongside the snippet's own `novalidate`: a cached or
      // hand-edited copy of the markup must never let the browser's
      // constraint bubble be the only feedback a shopper gets -- it is
      // invisible on mobile, which is exactly where this widget is used most.
      if (form.setAttribute) form.setAttribute("novalidate", "novalidate");
      form.addEventListener("submit", handleSubmit);
    }
    if (input) {
      input.addEventListener("input", function () {
        syncButtonEnabled();
        // Clear a stale "Enter a 4-digit Australian postcode." the moment the
        // shopper has fixed it. Only that message is cleared: a real result
        // stays on screen while they retype, exactly as before.
        if (
          lastResultStatus === "invalid_postcode" &&
          normalisePostcode(input.value) !== null
        ) {
          setResult(null);
        }
      });
      // BLUR: the second half of the fix. A shopper who types "99" and taps
      // away has told us they are done with the field, so the widget answers
      // then -- it does not wait for a submit that a partial postcode may
      // never produce. An EMPTY field says nothing (they may simply be
      // leaving the panel), and a message already on screen is not replaced
      // by a second copy of itself.
      input.addEventListener("blur", function () {
        var raw = String(input.value == null ? "" : input.value).trim();
        if (raw.length === 0) return;
        if (normalisePostcode(raw) !== null) return;
        if (lastResultStatus === "invalid_postcode") return;
        showInvalidPostcode();
      });
      syncButtonEnabled();
    }

    // Standard Dawn/Horizon variant-change signal: product-info.js updates the
    // hidden id input inside the product form and dispatches a bubbling native
    // "change" event on it whenever the shopper picks a different variant --
    // listening at the document level means this keeps working even though
    // this block lives outside that form. NOT debounced: a variant swap is a
    // single deliberate act.
    document.addEventListener("change", function (evt) {
      var target = evt.target;
      if (!target || !target.matches) return;
      if (target.matches('input[name="id"]')) {
        currentVariantId = target.value || "";
        applyClassForCurrentVariant();
        return;
      }
      if (container.hidden || !lastPostcode) return;
      if (container.contains(target)) return;
      var qtyEl = findQuantityInput(container);
      if (qtyEl && target === qtyEl) requestQuoteDebounced();
    });

    // Steppers commonly fire `input` (or only `change` on blur), so listen to
    // both; the 400ms debounce collapses a burst of clicks into one request.
    document.addEventListener("input", function (evt) {
      var target = evt.target;
      if (!target || container.hidden || !lastPostcode) return;
      if (container.contains(target)) return;
      var qtyEl = findQuantityInput(container);
      if (qtyEl && target === qtyEl) requestQuoteDebounced();
    });

    applyClassForCurrentVariant();
  }

  function init() {
    var containers = document.querySelectorAll("[data-scarlett-freight-notice]");
    for (var i = 0; i < containers.length; i++) {
      try {
        initNotice(containers[i]);
      } catch (err) {
        console.warn("[scarlett-freight] init failed for one notice:", err);
      }
    }
  }

  API.init = init;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})(typeof window !== "undefined" ? window : null);
